<script>
  import { LayerCake, Svg } from "layercake";
  import { Portal } from "bits-ui";
  import ScatterPlot from "./ChartParts/ScatterPlot.svelte";
  import ArrowLong from "./ChartParts/ArrowLong.svelte";
  import { AxisX, AxisY } from "@urbaninstitute/dataviz-components";
  import {
    LEFT_Y_AXIS_LABEL,
    LEFT_Y_AXIS_LABEL_INFO,
    LEFT_X_AXIS_LABEL,
    LEFT_CHART_BOUNDARY_TOOLTIP_LABEL
  } from "$utils/constants.js";
  import InfoIcon from "./InfoIcon.svelte";
  import { getContext } from "svelte";
  import { useData } from "$utils/data.svelte.js";
  import { getTier } from "$utils/tier.svelte.js";
  import { scaleBand, scaleLinear } from "d3";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import TooltipWrapper from "./ChartParts/TooltipWrapper.svelte";

  let toolData = useData();

  let yKey = "BoundaryPolicyRobustness05";
  let xDomain = $derived(
    toolData.activeSegregationMetric == "NormalizedExpWhiteBlack" ? [0, 0.72] : [0, 0.51]
  );

  let viewportWidth = $state(0);


  const r = 8;
  const padding = 10;
  const color = urbanColors.blue;
  const opacity = 0.5;
  let tooltip = $state(null);

  // App-owned tooltip layer (from Tool.svelte via context): a div outside the zoom:0.8 notebook
  // but inside this component's DOM/shadow root. Portaling here fixes both the zoom drift and the
  // CMS-embed "tooltip doesn't show" issue (no fragile document/.theme lookup that can return null).
  const getTooltipLayer = getContext("tooltipLayer");
  let portalTarget = $derived(getTooltipLayer?.());
</script>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="container">
  <div class="top-of-left-chart">
    <div class="axis-label">
      <div class="axis-label-main">
        <ArrowLong direction="up" /> <span class="axis-label-text">{LEFT_Y_AXIS_LABEL}
          <InfoIcon
            text={LEFT_Y_AXIS_LABEL_INFO}
            onOpenGlossary={() => (toolData.isGlossaryOpen = true)}
            nudge={-1}
          /></span>
      </div>
    </div>
  </div>
  {#if toolData.isReady() && toolData.leftScatterPlotData.length > 0}
    <div class="chart-container" >
      <LayerCake
        padding={{ top: 10, right: 5, bottom: 32, left: 25 }}
        x={toolData.activeSegregationMetric}
        y={yKey}
        {xDomain}
        yDomain={[0, 5]}
        xScale={scaleLinear()}
        yScale={scaleLinear()}
        xPadding={[padding, padding]}
        yPadding={[0, padding]}
        data={toolData.leftScatterPlotData}
        custom={{
          selectedID: toolData.activeDistrictID,
          onClick: (d) => (toolData.activeDistrictID = d.DistrictID),
          highlightSameState: toolData.highlightSameState,
          sameStateIDs: toolData.sameStateDistrictIDs
        }}
      >
        {#if tooltip && portalTarget}
          <Portal to={portalTarget}>
            <TooltipWrapper
              x={tooltip.x}
              y={tooltip.y}
              state={tooltip.data.State}
              name={tooltip.data.DistrictName}
              active_seg_value={tooltip.data[toolData.activeSegregationMetric]}
              active_seg_label={toolData.activeSegregationMetricLabel}
              extra_label={LEFT_CHART_BOUNDARY_TOOLTIP_LABEL}
              extra_value={tooltip.data.BoundaryPolicyRobustness05}
            />
          </Portal>
        {/if}
        <Svg>
          <AxisX gridlines={true} tickMarks={false} baseline={false} yTick={20} ticks={getTier(viewportWidth) === "mobile" ? 2 : 5}/>
          <AxisY gridlines={true} tickMarks={false} />
          <ScatterPlot {r} fill={color} {opacity} stroke="#696969" bind:tooltip />
          <!-- Redraw tick labels on top so the large grey/yellow highlight circles don't cover them; gridlines stay behind the data -->
          <AxisX gridlines={false} tickMarks={false} baseline={false} yTick={20} ticks={getTier(viewportWidth) === "mobile" ? 2 : 5}/>
          <AxisY gridlines={false} tickMarks={false} />
        </Svg>
      </LayerCake>
      <p class="x-axis-label-right">{LEFT_X_AXIS_LABEL}<ArrowLong /></p>

    </div>
  {/if}
</div>

<style>
  .container {
    height: auto;
    background: rgba(255, 255, 255, 0.72);
    align-items: flex-end;
  }

  .top-of-left-chart {
    padding-top: 20px;
  }

  .axis-label {
    width: 100%;
    box-sizing: border-box;
    padding: 0 15px;
    font-size: 14px;
  }

  /* On mobile the label is too long for one line, so allow it to wrap to two lines
     (the inline info icon then follows the text instead of extending off-screen), with
     a bit of line spacing. Keep the left shift so the up-arrow still lines up with the
     y-axis ticks. */
  @media (max-width: 799px) {
    .axis-label {
      padding-right: 2px;
    }
    /* Lay the arrow and the text out as separate flex items so the text forms its own
       column. Wrapped lines then all start at the same left edge (aligned under "More"),
       instead of returning to the arrow's edge. */
    .axis-label-main {
      display: flex;
      align-items: flex-start;
      gap: 0.25em;
      white-space: normal;
      line-height: 1.35;
      transform: translateX(-14px);
    }
    .axis-label-text {
      min-width: 0;
    }
  }


  /* No explicit display here: the div is block by default on desktop, and omitting it lets
     the mobile flex layout above win (equal specificity would otherwise let this later rule
     override the media query). */
  .axis-label-main {
    min-width: 0;
  }

  /* Nudges the up-arrow so it sits directly above the y-axis tick column
     (0/2/4), which sits a few px to the right of this label's text start. */
  .axis-label-main :global(svg.arrow-long.up) {
    transform: translateX(7px);
    flex-shrink: 0;
  }

  .chart-container {
    position: relative;
    height: 392px; /**This needs hardcoded even tho its container is, for LayerCake to work*/
    padding: 10px;
  }

  p.x-axis-label-right {
    position: absolute;
    right: 12px;
    bottom: 48px;
    margin: 0;
    font-size: 14px !important;
    line-height: normal;
    font-weight: var(--font-weight-normal);
    background-color: transparent;
    padding: 0 1px;
    white-space: nowrap;
    pointer-events: none;
  }
</style>
